import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Eye, Download, CheckCircle, FileText, Filter, X, Pencil, Trash2, Mail } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import i18n from "@/i18n";

const formatCurrency = (value) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol'
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const styles = {
    Aceptada: "bg-green-100 text-green-700",
    Pendiente: "bg-amber-100 text-amber-700",
    Rechazada: "bg-red-100 text-red-700"
  };
  const labels = {
    Aceptada: t('common.accepted'),
    Pendiente: t('common.pending'),
    Rechazada: t('common.rejected')
  };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded text-xs font-semibold ${styles[status] || styles.Pendiente}`}>
      {labels[status] || status}
    </span>
  );
};

export default function Facturas() {
  const { t } = useTranslation();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [createdFactura, setCreatedFactura] = useState(null);
  const [emailData, setEmailData] = useState({ recipient_email: "", recipient_name: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  const [formData, setFormData] = useState({
    cliente_nombre: "",
    cliente_nit: "",
    descripcion_servicio: "",
    cantidad: 1,
    valor_unitario: "",
    iva_porcentaje: 19,
    observaciones: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchFacturas = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter) params.append("estado", statusFilter);
      if (dateFrom) params.append("fecha_inicio", dateFrom);
      if (dateTo) params.append("fecha_fin", dateTo);
      
      const response = await axios.get(`${API}/facturas?${params.toString()}`);
      setFacturas(response.data);
    } catch (error) {
      toast.error(t('invoices.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFacturas();
  }, [searchTerm, statusFilter, dateFrom, dateTo]);

  const validateForm = () => {
    const errors = {};
    if (!formData.cliente_nombre.trim()) errors.cliente_nombre = t('common.required');
    if (!formData.cliente_nit.trim()) errors.cliente_nit = t('common.required');
    if (!formData.descripcion_servicio.trim()) errors.descripcion_servicio = t('common.required');
    if (!formData.cantidad || formData.cantidad <= 0) errors.cantidad = t('common.mustBeGreaterThanZero');
    if (!formData.valor_unitario || formData.valor_unitario <= 0) errors.valor_unitario = t('common.mustBeGreaterThanZero');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/facturas`, {
        ...formData,
        cantidad: parseFloat(formData.cantidad),
        valor_unitario: parseFloat(formData.valor_unitario),
        iva_porcentaje: parseInt(formData.iva_porcentaje)
      });
      
      setCreatedFactura(response.data);
      setShowCreateModal(false);
      setShowSuccessModal(true);
      setFormData({
        cliente_nombre: "",
        cliente_nit: "",
        descripcion_servicio: "",
        cantidad: 1,
        valor_unitario: "",
        iva_porcentaje: 19,
        observaciones: ""
      });
      fetchFacturas();
    } catch (error) {
      toast.error(t('invoices.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async (factura) => {
    try {
      const response = await axios.get(`${API}/facturas/${factura.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Factura_${factura.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('common.pdfDownloaded'));
    } catch (error) {
      toast.error(t('common.pdfDownloadError'));
    }
  };

  const subtotal = formData.cantidad * formData.valor_unitario || 0;
  const ivaAmount = subtotal * (formData.iva_porcentaje / 100);
  const total = subtotal + ivaAmount;

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const handleEdit = (factura) => {
    setSelectedFactura(factura);
    setFormData({
      cliente_nombre: factura.cliente_nombre,
      cliente_nit: factura.cliente_nit,
      descripcion_servicio: factura.descripcion_servicio,
      cantidad: factura.cantidad,
      valor_unitario: factura.valor_unitario,
      iva_porcentaje: factura.iva_porcentaje,
      observaciones: factura.observaciones || "",
      estado: factura.estado
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/facturas/${selectedFactura.id}`, {
        ...formData,
        cantidad: parseFloat(formData.cantidad),
        valor_unitario: parseFloat(formData.valor_unitario),
        iva_porcentaje: parseInt(formData.iva_porcentaje)
      });
      
      toast.success(t('invoices.updateSuccess'));
      setShowEditModal(false);
      resetForm();
      fetchFacturas();
    } catch (error) {
      toast.error(t('invoices.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/facturas/${selectedFactura.id}`);
      toast.success(t('invoices.deleteSuccess', { number: selectedFactura.numero }));
      setShowDeleteDialog(false);
      setSelectedFactura(null);
      fetchFacturas();
    } catch (error) {
      toast.error(t('invoices.deleteError'));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailData.recipient_email) {
      toast.error(t('common.enterValidEmail'));
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/facturas/${selectedFactura.id}/send-email`, emailData);
      toast.success(t('invoices.sentSuccess', { email: emailData.recipient_email }));
      setShowEmailModal(false);
      setEmailData({ recipient_email: "", recipient_name: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.sendError'));
    } finally {
      setSendingEmail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_nombre: "",
      cliente_nit: "",
      descripcion_servicio: "",
      cantidad: 1,
      valor_unitario: "",
      iva_porcentaje: 19,
      observaciones: ""
    });
    setFormErrors({});
  };

  return (
    <div data-testid="facturas-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow']">{t('invoices.title')}</h1>
          <p className="text-slate-500 mt-1">{t('invoices.subtitle')}</p>
        </div>
        <Button 
          data-testid="btn-nueva-factura"
          onClick={() => setShowCreateModal(true)}
          className="bg-[#0f2a4a] hover:bg-[#1e3a8a] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('invoices.newInvoice')}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  data-testid="search-input"
                  placeholder={t('invoices.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-[160px]">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="Aceptada">{t('common.accepted')}</SelectItem>
                <SelectItem value="Pendiente">{t('common.pending')}</SelectItem>
                <SelectItem value="Rechazada">{t('common.rejected')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[150px]"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[150px]"
            />
            {(searchTerm || statusFilter || dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> {t('common.clear')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : facturas.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">{t('invoices.noInvoicesFound')}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateModal(true)}
              >
                {t('invoices.createFirstInvoice')}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-600">{t('invoices.invoiceNumber')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('common.date')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('invoices.client')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('invoices.nit')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('invoices.service')}</TableHead>
                    <TableHead className="font-semibold text-slate-600 text-right">{t('common.total')}</TableHead>
                    <TableHead className="font-semibold text-slate-600">{t('common.status')}</TableHead>
                    <TableHead className="font-semibold text-slate-600 text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {facturas.map((factura) => (
                    <TableRow 
                      key={factura.id} 
                      data-testid={`factura-row-${factura.numero}`}
                      className="hover:bg-slate-50/50"
                    >
                      <TableCell className="font-medium text-slate-900">{factura.numero}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(factura.fecha)}</TableCell>
                      <TableCell className="text-slate-900">{factura.cliente_nombre}</TableCell>
                      <TableCell className="text-slate-600">{factura.cliente_nit}</TableCell>
                      <TableCell className="text-slate-600 max-w-[200px] truncate">{factura.descripcion_servicio}</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(factura.total)}</TableCell>
                      <TableCell><StatusBadge status={factura.estado} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" data-testid={`btn-ver-${factura.numero}`} onClick={() => { setSelectedFactura(factura); setShowViewModal(true); }} title={t('common.view')}><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-edit-${factura.numero}`} onClick={() => handleEdit(factura)} title={t('common.edit')}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-download-${factura.numero}`} onClick={() => handleDownloadPDF(factura)} title={t('common.downloadPdf')}><Download className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-email-${factura.numero}`} onClick={() => { setSelectedFactura(factura); setEmailData({ recipient_email: "", recipient_name: factura.cliente_nombre }); setShowEmailModal(true); }} title={t('common.sendByEmail')}><Mail className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`btn-delete-${factura.numero}`} onClick={() => { setSelectedFactura(factura); setShowDeleteDialog(true); }} className="text-red-600 hover:text-red-700 hover:bg-red-50" title={t('common.delete')}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('invoices.createTitle')}</DialogTitle>
            <DialogDescription>{t('invoices.createDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cliente_nombre">{t('invoices.clientName')}</Label>
                <Input id="cliente_nombre" data-testid="input-cliente-nombre" value={formData.cliente_nombre} onChange={(e) => setFormData({...formData, cliente_nombre: e.target.value})} className={formErrors.cliente_nombre ? "border-red-500" : ""} />
                {formErrors.cliente_nombre && <p className="text-red-500 text-xs mt-1">{formErrors.cliente_nombre}</p>}
              </div>
              <div>
                <Label htmlFor="cliente_nit">{t('invoices.clientNit')}</Label>
                <Input id="cliente_nit" data-testid="input-cliente-nit" value={formData.cliente_nit} onChange={(e) => setFormData({...formData, cliente_nit: e.target.value})} className={formErrors.cliente_nit ? "border-red-500" : ""} />
                {formErrors.cliente_nit && <p className="text-red-500 text-xs mt-1">{formErrors.cliente_nit}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="descripcion_servicio">{t('invoices.serviceDescription')}</Label>
              <Input id="descripcion_servicio" data-testid="input-descripcion" value={formData.descripcion_servicio} onChange={(e) => setFormData({...formData, descripcion_servicio: e.target.value})} className={formErrors.descripcion_servicio ? "border-red-500" : ""} />
              {formErrors.descripcion_servicio && <p className="text-red-500 text-xs mt-1">{formErrors.descripcion_servicio}</p>}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cantidad">{t('invoices.quantity')}</Label>
                <Input id="cantidad" type="number" min="1" step="0.01" data-testid="input-cantidad" value={formData.cantidad} onChange={(e) => setFormData({...formData, cantidad: e.target.value})} className={formErrors.cantidad ? "border-red-500" : ""} />
                {formErrors.cantidad && <p className="text-red-500 text-xs mt-1">{formErrors.cantidad}</p>}
              </div>
              <div>
                <Label htmlFor="valor_unitario">{t('invoices.unitValue')}</Label>
                <Input id="valor_unitario" type="number" min="0" step="1000" data-testid="input-valor" value={formData.valor_unitario} onChange={(e) => setFormData({...formData, valor_unitario: e.target.value})} className={formErrors.valor_unitario ? "border-red-500" : ""} />
                {formErrors.valor_unitario && <p className="text-red-500 text-xs mt-1">{formErrors.valor_unitario}</p>}
              </div>
              <div>
                <Label htmlFor="iva_porcentaje">{t('invoices.iva')}</Label>
                <Select value={String(formData.iva_porcentaje)} onValueChange={(v) => setFormData({...formData, iva_porcentaje: parseInt(v)})}>
                  <SelectTrigger data-testid="select-iva"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="19">19%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="observaciones">{t('common.observationsOptional')}</Label>
              <Textarea id="observaciones" data-testid="input-observaciones" value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} rows={3} />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('common.subtotal')}:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('invoices.iva').replace(' *', '')} ({formData.iva_porcentaje}%):</span>
                <span className="font-medium">{formatCurrency(ivaAmount)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>{t('common.total')}:</span>
                <span className="text-[#0f2a4a]">{formatCurrency(total)}</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>{t('common.cancel')}</Button>
              <Button type="submit" data-testid="btn-submit-factura" disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2">
                {submitting ? t('common.processing') : t('invoices.createButton')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Barlow']">{t('invoices.successTitle')}</h3>
            <p className="text-slate-600 mb-4">{t('invoices.successMessage', { number: createdFactura?.numero })}</p>
            <div className="bg-slate-50 p-4 rounded-lg text-left mb-4">
              <p className="text-sm"><span className="text-slate-500">{t('invoices.successClient')}</span> {createdFactura?.cliente_nombre}</p>
              <p className="text-sm"><span className="text-slate-500">{t('invoices.successTotal')}</span> {formatCurrency(createdFactura?.total || 0)}</p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowSuccessModal(false)}>{t('common.close')}</Button>
              <Button className="bg-[#0f2a4a] hover:bg-[#1e3a8a]" onClick={() => { if (createdFactura) handleDownloadPDF(createdFactura); }}>
                <Download className="w-4 h-4 mr-2" />{t('common.downloadPdf')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('invoices.previewTitle')} - {selectedFactura?.numero}</DialogTitle>
            <DialogDescription>{t('invoices.previewDescription')}</DialogDescription>
          </DialogHeader>
          {selectedFactura && (
            <div className="space-y-6">
              <div className="flex justify-between items-start pb-4 border-b">
                <div>
                  <h3 className="text-xl font-bold text-[#0f2a4a] font-['Barlow']">{t('company.name')}</h3>
                  <p className="text-sm text-slate-500">{t('company.taxIdLabel')}: {t('company.taxId')}</p>
                  <p className="text-sm text-slate-500">{t('company.address')}</p>
                  <p className="text-sm text-slate-500">{t('company.city')}</p>
                </div>
                <div className="text-right">
                  <h2 className="text-2xl font-bold text-[#0f2a4a] font-['Barlow']">{t('invoices.electronicInvoice')}</h2>
                  <p className="text-sm text-slate-600 mt-2">N°: {selectedFactura.numero}</p>
                  <p className="text-sm text-slate-600">{t('common.date')}: {formatDate(selectedFactura.fecha)}</p>
                  <StatusBadge status={selectedFactura.estado} />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs text-slate-500 uppercase mb-2">{t('invoices.billedTo')}</p>
                <p className="font-semibold text-slate-900">{selectedFactura.cliente_nombre}</p>
                <p className="text-sm text-slate-600">{t('invoices.nitCedula')} {selectedFactura.cliente_nit}</p>
              </div>
              <table className="w-full">
                <thead className="bg-[#0f2a4a] text-white">
                  <tr>
                    <th className="text-left p-3 text-sm">{t('invoices.description')}</th>
                    <th className="text-center p-3 text-sm">{t('invoices.quantityShort')}</th>
                    <th className="text-right p-3 text-sm">{t('invoices.unitValueShort')}</th>
                    <th className="text-right p-3 text-sm">{t('common.subtotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3">{selectedFactura.descripcion_servicio}</td>
                    <td className="p-3 text-center">{selectedFactura.cantidad}</td>
                    <td className="p-3 text-right">{formatCurrency(selectedFactura.valor_unitario)}</td>
                    <td className="p-3 text-right font-semibold">{formatCurrency(selectedFactura.subtotal)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('common.subtotal')}:</span>
                    <span>{formatCurrency(selectedFactura.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('invoices.iva').replace(' *', '')} ({selectedFactura.iva_porcentaje}%):</span>
                    <span>{formatCurrency(selectedFactura.iva_valor)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2 bg-[#0f2a4a] text-white p-3 rounded">
                    <span>{t('common.total').toUpperCase()}:</span>
                    <span>{formatCurrency(selectedFactura.total)}</span>
                  </div>
                </div>
              </div>
              {selectedFactura.observaciones && (
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#0f2a4a]">
                  <p className="text-xs text-slate-500 uppercase mb-1">{t('common.observations')}</p>
                  <p className="text-sm text-slate-700">{selectedFactura.observaciones}</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowViewModal(false)}>{t('common.close')}</Button>
                <Button className="bg-[#0f2a4a] hover:bg-[#1e3a8a]" onClick={() => handleDownloadPDF(selectedFactura)}>
                  <Download className="w-4 h-4 mr-2" />{t('common.downloadPdf')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('invoices.editTitle')} {selectedFactura?.numero}</DialogTitle>
            <DialogDescription>{t('invoices.editDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_cliente_nombre">{t('invoices.clientName')}</Label>
                <Input id="edit_cliente_nombre" value={formData.cliente_nombre} onChange={(e) => setFormData({...formData, cliente_nombre: e.target.value})} className={formErrors.cliente_nombre ? "border-red-500" : ""} />
              </div>
              <div>
                <Label htmlFor="edit_cliente_nit">{t('invoices.clientNit')}</Label>
                <Input id="edit_cliente_nit" value={formData.cliente_nit} onChange={(e) => setFormData({...formData, cliente_nit: e.target.value})} className={formErrors.cliente_nit ? "border-red-500" : ""} />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_descripcion">{t('invoices.serviceDescription')}</Label>
              <Input id="edit_descripcion" value={formData.descripcion_servicio} onChange={(e) => setFormData({...formData, descripcion_servicio: e.target.value})} className={formErrors.descripcion_servicio ? "border-red-500" : ""} />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label htmlFor="edit_cantidad">{t('invoices.quantity')}</Label>
                <Input id="edit_cantidad" type="number" min="1" step="0.01" value={formData.cantidad} onChange={(e) => setFormData({...formData, cantidad: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="edit_valor">{t('invoices.unitValue')}</Label>
                <Input id="edit_valor" type="number" min="0" step="1000" value={formData.valor_unitario} onChange={(e) => setFormData({...formData, valor_unitario: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="edit_iva">{t('invoices.iva')}</Label>
                <Select value={String(formData.iva_porcentaje)} onValueChange={(v) => setFormData({...formData, iva_porcentaje: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="19">19%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_estado">{t('common.status')}</Label>
                <Select value={formData.estado} onValueChange={(v) => setFormData({...formData, estado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aceptada">{t('common.accepted')}</SelectItem>
                    <SelectItem value="Pendiente">{t('common.pending')}</SelectItem>
                    <SelectItem value="Rechazada">{t('common.rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="edit_observaciones">{t('common.observations')}</Label>
              <Textarea id="edit_observaciones" value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} rows={2} />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('common.subtotal')}:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('invoices.iva').replace(' *', '')} ({formData.iva_porcentaje}%):</span>
                <span className="font-medium">{formatCurrency(ivaAmount)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>{t('common.total')}:</span>
                <span className="text-[#0f2a4a]">{formatCurrency(total)}</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2">
                {submitting ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.deleteTitle', { number: selectedFactura?.numero })}</AlertDialogTitle>
            <AlertDialogDescription>{t('invoices.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('invoices.emailTitle')}</DialogTitle>
            <DialogDescription>{t('invoices.emailDescription', { number: selectedFactura?.numero })}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <Label htmlFor="recipient_email">{t('common.recipientEmail')}</Label>
              <Input id="recipient_email" type="email" placeholder={t('common.emailPlaceholder')} value={emailData.recipient_email} onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})} required />
            </div>
            <div>
              <Label htmlFor="recipient_name">{t('common.recipientName')}</Label>
              <Input id="recipient_name" placeholder={t('common.nameOptional')} value={emailData.recipient_name} onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})} />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg text-sm">
              <p className="text-slate-600">{t('invoices.emailWillSend', { number: selectedFactura?.numero })}</p>
              <ul className="mt-2 text-slate-500 list-disc list-inside">
                <li>{t('invoices.emailAttachment')}</li>
                <li>{t('invoices.emailSummary')}</li>
                <li>{t('invoices.emailContact')}</li>
              </ul>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEmailModal(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={sendingEmail} className="bg-[#0f2a4a] hover:bg-[#1e3a8a]">
                {sendingEmail ? t('common.sending') : <><Mail className="w-4 h-4 mr-2" />{t('common.sendEmail')}</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
